import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, UserPlus, Phone, Award, Trash2, Plus, History, Mail, Pencil, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Switch } from '../../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomersPage = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: '', surname: '', phone: '', email: '', can_use_account_payment: false });
  const [newCustomer, setNewCustomer] = useState({ phone: '', first_name: '', surname: '', email: '', gdpr_consent: true });
  const [manualPoints, setManualPoints] = useState({ points: 0, reason: '' });

  useEffect(() => { fetchCustomers(); }, [search]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const url = search ? `${API}/customers?search=${encodeURIComponent(search)}` : `${API}/customers`;
      const res = await axios.get(url);
      setCustomers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newCustomer.phone || !newCustomer.first_name || !newCustomer.surname) {
      toast.error('All fields are required'); return;
    }
    if (!newCustomer.gdpr_consent) {
      toast.error('GDPR consent is required'); return;
    }
    try {
      await axios.post(`${API}/customers`, newCustomer);
      toast.success('Customer created');
      setShowAddModal(false);
      setNewCustomer({ phone: '', first_name: '', surname: '', email: '', gdpr_consent: true });
      fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const viewDetail = async (customer) => {
    setSelectedCustomer(customer);
    setEditForm({
      first_name: customer.first_name || '',
      surname: customer.surname || '',
      phone: customer.phone || '',
      email: customer.email || '',
      can_use_account_payment: customer.can_use_account_payment || false
    });
    setEditing(false);
    try {
      const res = await axios.get(`${API}/customers/${customer.id}/history`);
      setPointsHistory(res.data);
    } catch (err) { console.error(err); }
    setShowDetailModal(true);
  };

  const handleEditSave = async () => {
    try {
      const res = await axios.put(`${API}/customers/${selectedCustomer.id}`, editForm);
      toast.success('Customer updated');
      setSelectedCustomer(res.data);
      setEditing(false);
      fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleAddPoints = async () => {
    if (!manualPoints.points || !manualPoints.reason) {
      toast.error('Points and reason are required'); return;
    }
    try {
      const res = await axios.post(`${API}/customers/${selectedCustomer.id}/points`, manualPoints);
      toast.success(`${manualPoints.points} points added`);
      setSelectedCustomer(res.data);
      setShowPointsModal(false);
      setManualPoints({ points: 0, reason: '' });
      fetchCustomers();
      const histRes = await axios.get(`${API}/customers/${selectedCustomer.id}/history`);
      setPointsHistory(histRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('This will permanently delete all customer data (GDPR). Continue?')) return;
    try {
      await axios.delete(`${API}/customers/${customerId}`);
      toast.success('Customer data deleted');
      setShowDetailModal(false);
      fetchCustomers();
    } catch (err) { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="customers-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Customers</h1>
          <p className="text-slate-500 mt-1 font-body">Loyalty programme members</p>
        </div>
        <Button data-testid="add-customer-btn" onClick={() => setShowAddModal(true)}>
          <UserPlus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="customer-search"
          placeholder="Search by name or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 text-center py-12 text-slate-400">Loading...</div>
        ) : customers.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-slate-400">
            {search ? 'No customers found' : 'No customers yet. Add your first customer!'}
          </div>
        ) : customers.map((c) => (
          <Card
            key={c.id}
            data-testid={`customer-card-${c.id}`}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => viewDetail(c)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-semibold text-slate-900">{c.first_name} {c.surname}</p>
                  <div className="flex items-center gap-1 mt-1 text-sm text-slate-500">
                    <Phone className="w-3 h-3" /> {c.phone}
                  </div>
                  {c.email && (
                    <div className="flex items-center gap-1 mt-0.5 text-xs text-slate-400">
                      <Mail className="w-3 h-3" /> {c.email}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-amber-600">
                    <Award className="w-4 h-4" />
                    <span className="font-heading font-bold text-lg">{c.points_balance}</span>
                  </div>
                  <p className="text-xs text-slate-400">points</p>
                </div>
              </div>
              {c.pending_pence > 0 && (
                <p className="text-xs text-slate-400 mt-2">{c.pending_pence}p pending towards next point</p>
              )}
              {c.can_use_account_payment && (
                <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                  <CheckCircle className="w-3 h-3" /> Account Customer
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Customer Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
            <DialogDescription>Register a new loyalty programme member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Phone Number *</Label>
              <Input data-testid="new-cust-phone" placeholder="07700 900 123" value={newCustomer.phone}
                onChange={(e) => setNewCustomer(p => ({...p, phone: e.target.value}))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input data-testid="new-cust-first" value={newCustomer.first_name}
                  onChange={(e) => setNewCustomer(p => ({...p, first_name: e.target.value}))} />
              </div>
              <div>
                <Label>Surname *</Label>
                <Input data-testid="new-cust-surname" value={newCustomer.surname}
                  onChange={(e) => setNewCustomer(p => ({...p, surname: e.target.value}))} />
              </div>
            </div>
            <div>
              <Label>Email Address</Label>
              <Input data-testid="new-cust-email" type="email" placeholder="name@hlag.com" value={newCustomer.email}
                onChange={(e) => setNewCustomer(p => ({...p, email: e.target.value}))} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={newCustomer.gdpr_consent}
                onChange={(e) => setNewCustomer(p => ({...p, gdpr_consent: e.target.checked}))}
                className="rounded border-slate-300" />
              Customer consents to data storage for loyalty purposes (GDPR)
            </label>
            <Button data-testid="save-customer-btn" className="w-full" onClick={handleCreate}>
              Create Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(v) => { setShowDetailModal(v); if (!v) setEditing(false); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCustomer?.first_name} {selectedCustomer?.surname}</DialogTitle>
            <DialogDescription>
              {selectedCustomer?.phone}
              {selectedCustomer?.email && ` · ${selectedCustomer.email}`}
            </DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4 mt-2">
              {/* Edit form */}
              {editing ? (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">First Name</Label>
                      <Input data-testid="edit-cust-first" value={editForm.first_name}
                        onChange={(e) => setEditForm(p => ({...p, first_name: e.target.value}))} />
                    </div>
                    <div>
                      <Label className="text-xs">Surname</Label>
                      <Input data-testid="edit-cust-surname" value={editForm.surname}
                        onChange={(e) => setEditForm(p => ({...p, surname: e.target.value}))} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input data-testid="edit-cust-phone" value={editForm.phone}
                      onChange={(e) => setEditForm(p => ({...p, phone: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input data-testid="edit-cust-email" type="email" placeholder="name@hlag.com" value={editForm.email}
                      onChange={(e) => setEditForm(p => ({...p, email: e.target.value}))} />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-xs">Account Payment Access</Label>
                      <p className="text-[10px] text-slate-400">Allow this customer to pay on account</p>
                    </div>
                    <Switch data-testid="edit-cust-account-toggle"
                      checked={editForm.can_use_account_payment}
                      onCheckedChange={(checked) => setEditForm(p => ({...p, can_use_account_payment: checked}))} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" data-testid="save-edit-customer-btn" onClick={handleEditSave}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-xs text-amber-600">Balance</p>
                    <p className="text-2xl font-bold text-amber-700">{selectedCustomer.points_balance}</p>
                    <p className="text-xs text-amber-500">points</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500">Total Earned</p>
                    <p className="text-xl font-bold text-slate-700">{selectedCustomer.points_earned_total}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500">Redeemed</p>
                    <p className="text-xl font-bold text-slate-700">{selectedCustomer.points_redeemed_total}</p>
                  </div>
                </div>
              )}

              {selectedCustomer.pending_pence > 0 && (
                <p className="text-sm text-slate-500">{selectedCustomer.pending_pence}p accumulating towards next point</p>
              )}

              {user?.role === 'admin' && !editing && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="edit-customer-btn"
                    onClick={() => setEditing(true)}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" data-testid="manual-points-btn"
                    onClick={() => setShowPointsModal(true)}>
                    <Plus className="w-3 h-3 mr-1" /> Add Points
                  </Button>
                  <Button size="sm" variant="destructive" data-testid="delete-customer-btn"
                    onClick={() => handleDelete(selectedCustomer.id)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete (GDPR)
                  </Button>
                </div>
              )}

              <div>
                <h3 className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-1">
                  <History className="w-4 h-4" /> Points History
                </h3>
                {pointsHistory.length === 0 ? (
                  <p className="text-sm text-slate-400">No history yet</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {pointsHistory.map((h) => (
                      <div key={h.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                        <div>
                          <p className="font-medium text-slate-700">{h.reason}</p>
                          <p className="text-xs text-slate-400">
                            {new Date(h.created_at).toLocaleString('en-GB')} by {h.by_user}
                          </p>
                        </div>
                        <span className={`font-mono font-bold ${h.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {h.points >= 0 ? '+' : ''}{h.points}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Points Modal */}
      <Dialog open={showPointsModal} onOpenChange={setShowPointsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Points Manually</DialogTitle>
            <DialogDescription>
              Add bonus points to {selectedCustomer?.first_name} {selectedCustomer?.surname}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Points to Add</Label>
              <Input data-testid="manual-points-amount" type="number" min="1"
                value={manualPoints.points}
                onChange={(e) => setManualPoints(p => ({...p, points: parseInt(e.target.value) || 0}))} />
            </div>
            <div>
              <Label>Reason</Label>
              <Input data-testid="manual-points-reason" placeholder="e.g. Goodwill gesture"
                value={manualPoints.reason}
                onChange={(e) => setManualPoints(p => ({...p, reason: e.target.value}))} />
            </div>
            <Button data-testid="confirm-add-points-btn" className="w-full" onClick={handleAddPoints}>
              Add {manualPoints.points} Points
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
